import { cn } from "@/lib/utils";

/**
 * A short list of choices laid out in the form itself.
 *
 * For two to five options an overlay is pure cost on a phone: it is a second
 * tap, it competes with the keyboard for the screen, and Radix's select closes
 * itself when the keyboard hiding resizes the window. Chips cost one tap and
 * show every option and the current value at once.
 *
 * `segmented` lays the options out as one equal-width row (expense / income,
 * weekly / monthly…); `chips` wraps them, which suits names of uneven length
 * such as accounts. An option may carry its own `activeClassName` where the
 * choice has a colour of its own.
 */
export function ChoiceChips({
  label, options, value, onValueChange, variant = "chips", className,
}) {
  const segmented = variant === "segmented";
  return (
    <div
      role="radiogroup"
      aria-label={label}
      className={cn(
        segmented ? "grid gap-1 rounded-md bg-secondary p-1" : "flex flex-wrap gap-2",
        className,
      )}
      style={segmented ? { gridTemplateColumns: `repeat(${options.length}, minmax(0, 1fr))` } : undefined}
    >
      {options.map((option) => (
        <Chip
          key={option.value}
          option={option}
          checked={option.value === value}
          segmented={segmented}
          onSelect={() => onValueChange(option.value)}
        />
      ))}
    </div>
  );
}

function Chip({ option, checked, segmented, onSelect }) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={checked}
      data-testid={option.testid}
      onClick={onSelect}
      className={cn(
        "flex min-h-10 items-center justify-center gap-1.5 rounded text-sm font-semibold transition-colors touch:min-h-11",
        segmented ? segmentClassName(option, checked) : chipClassName(checked),
      )}
    >
      {option.icon}
      <span className="truncate">{option.label}</span>
    </button>
  );
}

function segmentClassName(option, checked) {
  if (!checked) return "px-2 text-muted-foreground active:bg-background/40";
  return cn("px-2", option.activeClassName || "bg-background text-foreground shadow-sm");
}

function chipClassName(checked) {
  return cn(
    "max-w-full rounded-full border px-4",
    checked
      ? "border-income bg-income/10 text-foreground"
      : "border-border bg-background text-muted-foreground active:bg-secondary/60",
  );
}
